import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ;
const COUPON_API = `${API_BASE_URL}/api/coupons`;

const initialForm = {
  code: "",
  type: "percentage",
  value: "",
  minOrderAmount: "",
  maxDiscount: "",
  expiryDate: "",
  usageLimit: "",
  isActive: true,
};

function CouponsManager() {
  const token = localStorage.getItem("token");

  const [coupons, setCoupons] = useState([]);
  const [search, setSearch] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState(initialForm);

  const authHeaders = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
    }),
    [token]
  );

  const fetchCoupons = async () => {
    try {
      setLoading(true);

      const { data } = await axios.get(COUPON_API, {
        params: { search: search.trim() },
        headers: authHeaders,
      });

      setCoupons(data?.coupons || data || []);
    } catch (err) {
      console.error("Fetch coupons error:", err);
      alert("Unable to fetch coupons.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCoupons();
  }, [search, authHeaders]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(initialForm);
    setShowModal(true);
  };

  const openEdit = (coupon) => {
    setEditingId(coupon._id || coupon.id);
    setForm({
      code: coupon.code || "",
      type: coupon.type || "percentage",
      value: coupon.value || "",
      minOrderAmount: coupon.minOrderAmount || "",
      maxDiscount: coupon.maxDiscount || "",
      expiryDate: coupon.expiryDate ? coupon.expiryDate.slice(0, 10) : "",
      usageLimit: coupon.usageLimit || "",
      isActive: coupon.isActive !== false,
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingId(null);
    setForm(initialForm);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setSubmitting(true);

      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
      };

      if (editingId) {
        await axios.put(`${COUPON_API}/${editingId}`, payload, {
          headers: authHeaders,
        });
        alert("Coupon updated successfully.");
      } else {
        await axios.post(COUPON_API, payload, {
          headers: authHeaders,
        });
        alert("Coupon created successfully.");
      }

      closeModal();
      fetchCoupons();
    } catch (err) {
      console.error("Save coupon error:", err);
      alert(err.response?.data?.message || "Unable to save coupon.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Delete this coupon?");
    if (!confirmed) return;

    try {
      await axios.delete(`${COUPON_API}/${id}`, {
        headers: authHeaders,
      });
      alert("Coupon deleted successfully.");
      fetchCoupons();
    } catch (err) {
      console.error("Delete coupon error:", err);
      alert(err.response?.data?.message || "Unable to delete coupon.");
    }
  };

  return (
    <div className="manager-section">
      <div className="manager-topbar">
        <div>
          <h2>Coupons</h2>
          <p>
            Create discount codes with percentage or fixed values, usage limits,
            and expiry dates.
          </p>
        </div>

        <button className="theme-btn" onClick={openCreate} type="button">
          Add Coupon
        </button>
      </div>

      <div className="manager-tools">
        <div className="manager-search-wrap">
          <input
            type="text"
            className="manager-search"
            placeholder="Search coupons by code..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
      </div>

      <div className="manager-table-wrap">
        <table className="manager-table">
          <thead>
            <tr>
              <th>Code</th>
              <th>Type</th>
              <th>Value</th>
              <th>Min Order</th>
              <th>Expiry</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>

          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">Loading coupons...</td>
              </tr>
            ) : coupons.length === 0 ? (
              <tr>
                <td colSpan="7">
                  <div className="empty-state">No coupons found.</div>
                </td>
              </tr>
            ) : (
              coupons.map((item) => (
                <tr key={item._id || item.id}>
                  <td>{item.code}</td>
                  <td>{item.type}</td>
                  <td>{item.value}</td>
                  <td>{item.minOrderAmount || 0}</td>
                  <td>
                    {item.expiryDate
                      ? new Date(item.expiryDate).toLocaleDateString()
                      : "-"}
                  </td>
                  <td>
                    <span
                      className={`status-badge ${
                        item.isActive ? "status-active" : "status-inactive"
                      }`}
                    >
                      {item.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions">
                      <button
                        className="outline-btn"
                        type="button"
                        onClick={() => openEdit(item)}
                      >
                        Edit
                      </button>
                      <button
                        className="danger-btn"
                        type="button"
                        onClick={() => handleDelete(item._id || item.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-backdrop">
          <div className="modal-card small">
            <div className="modal-head">
              <div>
                <h3>{editingId ? "Edit Coupon" : "Add Coupon"}</h3>
                <p>
                  Configure code type, value, expiry date, and order conditions.
                </p>
              </div>
              <button className="modal-close" onClick={closeModal} type="button">
                ✕
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Coupon Code</label>
                  <input
                    className="manager-input"
                    name="code"
                    value={form.code}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Type</label>
                  <select
                    className="manager-select"
                    name="type"
                    value={form.type}
                    onChange={handleChange}
                  >
                    <option value="percentage">Percentage</option>
                    <option value="fixed">Fixed</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Value</label>
                  <input
                    type="number"
                    className="manager-input"
                    name="value"
                    value={form.value}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Minimum Order Amount</label>
                  <input
                    type="number"
                    className="manager-input"
                    name="minOrderAmount"
                    value={form.minOrderAmount}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Max Discount</label>
                  <input
                    type="number"
                    className="manager-input"
                    name="maxDiscount"
                    value={form.maxDiscount}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Usage Limit</label>
                  <input
                    type="number"
                    className="manager-input"
                    name="usageLimit"
                    value={form.usageLimit}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group full">
                  <label>Expiry Date</label>
                  <input
                    type="date"
                    className="manager-input"
                    name="expiryDate"
                    value={form.expiryDate}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group full">
                  <div className="checkbox-row">
                    <label>
                      <input
                        type="checkbox"
                        name="isActive"
                        checked={form.isActive}
                        onChange={handleChange}
                      />
                      Active
                    </label>
                  </div>
                </div>
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="outline-btn"
                  onClick={closeModal}
                >
                  Cancel
                </button>
                <button type="submit" className="theme-btn" disabled={submitting}>
                  {submitting
                    ? "Saving..."
                    : editingId
                    ? "Update Coupon"
                    : "Create Coupon"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CouponsManager;